import { NavLink } from "react-router-dom";

import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { NumberedListCard, type NumberedListItem } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { SignalStrip } from "../components/SignalStrip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api, type FinancialSectionData, type FinancialTableGroupData } from "../lib/api";

type TeamAskPageData = Awaited<ReturnType<typeof loadTeamAskPage>>;

async function loadTeamAskPage() {
  return api.getFinancials();
}

function flattenDisclosures(section: FinancialSectionData) {
  return [
    ...section.disclosureNotes,
    ...section.unresolvedItems.map((item) => `${item.label}: ${item.value}`)
  ];
}

function findGroup(section: FinancialSectionData, title: string) {
  const lowered = title.toLowerCase();
  return section.tableGroups.find((group) => group.title.toLowerCase().includes(lowered));
}

function buildTableRows(group: FinancialTableGroupData | undefined) {
  if (!group) {
    return [];
  }

  return group.rows.map((row) => group.columns.map((column) => row[column] ?? ""));
}

function findDatumValue(section: FinancialSectionData, patterns: string[]) {
  const loweredPatterns = patterns.map((pattern) => pattern.toLowerCase());
  const item = [
    ...section.verifiedFacts,
    ...section.modeledAssumptions,
    ...section.derivedOutputs,
    ...section.unresolvedItems
  ].find((datum) => {
    const label = datum.label.toLowerCase();
    return loweredPatterns.some((pattern) => label === pattern || label.includes(pattern));
  });

  return item?.value ?? "Not surfaced";
}

function extractExcludedItems(section: FinancialSectionData, costBreakdown: FinancialSectionData) {
  return [...flattenDisclosures(section), ...flattenDisclosures(costBreakdown)]
    .filter((note) => note.toLowerCase().startsWith("excluded"))
    .map((note) => note.replace(/^Excluded[^:]*:\s*/i, "").trim());
}

function TeamAskContent({ data }: { data: TeamAskPageData }) {
  const {
    projectFacts,
    developmentBudget,
    costBreakdown,
    underwritingScenarios,
    assumptionsAndDisclosures
  } = data;

  const developmentBudgetGroup = findGroup(developmentBudget, "combined current selected development budget");
  const costTopLevelGroup = findGroup(costBreakdown, "top-level cost structure");
  const budgetTotal = findDatumValue(developmentBudget, ["current selected development-budget total"]);
  const hardCostTotal = findDatumValue(developmentBudget, ["final selected hard-cost total"]);
  const softCostTotal = findDatumValue(developmentBudget, ["final selected soft-cost total"]);
  const baseYield = findDatumValue(underwritingScenarios, ["base yield on current selected development budget"]);
  const baseNoi = findDatumValue(underwritingScenarios, ["base noi", "base-case noi"]);
  const managementFee = findDatumValue(assumptionsAndDisclosures, ["management fee"]);
  const excludedItems = extractExcludedItems(developmentBudget, costBreakdown);

  const credibilitySignals: NumberedListItem[] = [
    {
      label: "Final investor-room artifacts already exist for the budget, operating model, underwriting, fallback case, and assumptions register.",
      detail: "Execution support is no longer just memo language. The current package is backed by synchronized investor-room artifacts and public promoted objects."
    },
    {
      label: "Sponsor-confirmed natural-gas availability is already logged as a tagged project fact for the lot.",
      detail: "The record carries sponsor / owner confirmation directly into site and cost logic rather than leaving utility access as a hypothetical."
    },
    {
      label: "The base stabilized case assumes third-party professional management instead of free self-management.",
      detail: "Hallin & Co. provides current local owner-facing management support at 18% to 20% of gross rental income, with guest-paid cleaning treated separately."
    },
    {
      label: "City fee, permit, utility, and tax mechanics are already locked from official Lynchburg sources.",
      detail: "That reduces execution guesswork in the current use-of-funds basis and keeps the sponsor from relying on generic fee placeholders."
    }
  ];

  const openItems: NumberedListItem[] = [
    {
      label: "Named sponsor / principal identity is not yet public-ready in the governed website package.",
      detail: "The diligence record references sponsor / owner confirmation, but it does not yet expose a final public biography or entity package."
    },
    {
      label: "Exact equity ask is not yet public-ready.",
      detail: "The current public package supports the selected development-budget basis, not a final named raise amount."
    },
    {
      label: "Debt sizing, pricing, and leverage terms are not yet public-ready.",
      detail: "The current underwriting is explicitly unlevered and should not be misread as a levered return or debt-service model."
    },
    {
      label: "Land basis, furnishings, financing / carry, and sales / marketing remain outside the current public budget boundary.",
      detail: "Those exclusions are deliberate and stay visible so the page does not imply a fully loaded all-in capital stack."
    }
  ];

  return (
    <div className="stack-xl">
      <PremiumHero
        eyebrow="Team & Ask"
        title="Execution path is visible. Capital boundary is explicit."
        subtitle={`The current public package supports a sponsor-led ${projectFacts.leadConcept.toLowerCase()} execution path, a professional-management operating model, and a selected project-cost basis of ${budgetTotal}. Named sponsor identity, exact equity ask, and debt sizing are not yet public-ready, so this surface shows the current execution and funding boundary without inventing the stack.`}
        backgroundImage="/comps/110-f-st-streetview.jpg"
        backgroundLabel="Grounded local property context"
        badges={
          <>
            <SignalPill label={projectFacts.leadConcept} tone="positive" />
            <SignalPill label="Unlevered public underwriting" tone="neutral" />
            <SignalPill label="Financing stack partially public" tone="warning" />
          </>
        }
        actions={
          <>
            <NavLink to="/investment-case" className="link-button link-button-primary">
              Return to Investment Case
            </NavLink>
            <NavLink to="/financials" className="link-button link-button-secondary">
              Return to Financials
            </NavLink>
          </>
        }
        aside={
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Current public read</span>
              <div style={{ fontSize: "1.7rem", lineHeight: 1.05, letterSpacing: "-0.04em", color: "#f6fbff" }}>
                Sponsor package is partial. Budget boundary is not.
              </div>
              <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                This page shows what is already supportable: execution posture, current budget basis, use-of-funds structure, and the financing details that remain outside the public package.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px"
              }}
            >
              {[
                ["Sponsor package", "Partial public read"],
                ["Operating path", "Third-party management"],
                ["Public budget basis", budgetTotal],
                ["Base-case yield", baseYield]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: "18px",
                    padding: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ color: "rgba(216, 227, 244, 0.7)", fontSize: "0.9rem", lineHeight: 1.55 }}>
              Exact debt and equity sizing are still outside the governed public package. The current investor-facing funding basis is the selected development-budget total and its stated exclusions.
            </div>
          </div>
        }
      />

      <SignalStrip
        items={[
          {
            label: "Sponsor package",
            value: "Partial public read",
            note: "Named sponsor / principal identity is not yet public-ready.",
            tone: "warning"
          },
          {
            label: "Management posture",
            value: "Third-party professional management",
            note: `Current management-fee assumption: ${managementFee}`,
            tone: "positive"
          },
          {
            label: "Public budget basis",
            value: budgetTotal,
            note: "Current selected development-budget total",
            tone: "neutral"
          },
          {
            label: "Base-case yield",
            value: baseYield,
            note: `Current base-case NOI: ${baseNoi}`,
            tone: "positive"
          },
          {
            label: "Excluded capital layers",
            value: `${excludedItems.length}`,
            note: "Land, furnishings, financing / carry, and other still-excluded layers stay visible.",
            tone: "warning"
          }
        ]}
      />

      <section className="stack-lg">
        <div className="stack-xs">
          <span className="card-eyebrow">Who Is Executing</span>
          <h2 className="card-title">The execution path is visible even though the named sponsor package is not fully public yet.</h2>
          <p className="card-copy" style={{ maxWidth: "60ch" }}>
            This page does not invent biographies or legal structure. It shows the current public execution stack, the current sponsor-backed facts in the record, and the local operating support already present in the diligence package.
          </p>
        </div>

        <DataTableCard
          eyebrow="Execution Stack"
          title="Current public execution read"
          description="Grounded sponsor and operator context that can already be shown without overstating certainty."
          columns={["Execution item", "Current read", "Boundary / note"]}
          rows={[
            [
              "Named sponsor / principal",
              "Not yet public-ready",
              "No governed public object currently exposes the final public sponsor identity package."
            ],
            [
              "Current sponsor role",
              "Sponsor / owner confirmation appears in the diligence record as project-fact support",
              "Current source-log support is factual and execution-relevant, but it is not yet a public biography surface."
            ],
            [
              "Operating path",
              "Third-party professional management in the base stabilized case",
              "Current model avoids assuming free self-management."
            ],
            [
              "Local operating support",
              "Hallin & Co. owner-facing management page supports current market-practice treatment",
              "Useful as local operating support, not as an executed contract."
            ]
          ]}
        />
      </section>

      <section className="stack-lg">
        <div className="stack-xs">
          <span className="card-eyebrow">Why This Team Is Credible</span>
          <h2 className="card-title">Current credibility comes from execution discipline, local operating support, and an evidence-backed package.</h2>
          <p className="card-copy" style={{ maxWidth: "60ch" }}>
            The current public read is not built on generic praise. It is built on what the source set actually shows about execution readiness, cost discipline, and local operating realism.
          </p>
        </div>

        <NumberedListCard
          eyebrow="Execution Support"
          title="Grounded credibility supports"
          description="These are the strongest current supports for execution credibility in the repo and source set."
          items={credibilitySignals}
          tone="positive"
        />
      </section>

      <section className="stack-lg">
        <div className="stack-xs">
          <span className="card-eyebrow">Capital Ask</span>
          <h2 className="card-title">The current public package exposes the funding boundary, not a final raise amount.</h2>
          <p className="card-copy" style={{ maxWidth: "60ch" }}>
            Investors can now see the current project-cost basis, the supporting operating and return read, and the financing details that are still withheld. The page does not invent equity or debt terms that the public package does not carry.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
          }}
        >
          <MetricCard title="Public budget basis" value={budgetTotal} detail="Current selected development-budget total" />
          <MetricCard title="Hard costs" value={hardCostTotal} detail="Current selected hard-cost total" />
          <MetricCard title="Soft costs" value={softCostTotal} detail="Current selected soft-cost total" />
          <MetricCard title="Base-case yield" value={baseYield} detail="Yield on the current selected development-budget basis" />
        </div>

        <DataTableCard
          eyebrow="Capital Structure Posture"
          title="What can be shown now"
          description="Current investor-facing financing posture from the governed source set."
          columns={["Capital item", "Current read", "Disclosure"]}
          rows={[
            [
              "Exact equity ask",
              "Not yet public-ready",
              "No governed public object currently exposes a named raise amount."
            ],
            [
              "Debt structure",
              "Not yet public-ready",
              "Current underwriting is explicitly unlevered and not a debt-service model."
            ],
            [
              "Current public funding basis",
              budgetTotal,
              "Selected development-budget total currently anchors the public capital read."
            ],
            [
              "Current investor-facing return read",
              `${baseYield} yield on cost`,
              "Still based on the current selected development-budget basis rather than a levered capital stack."
            ]
          ]}
        />
      </section>

      <section className="stack-lg">
        <div className="stack-xs">
          <span className="card-eyebrow">Sources and Uses / Use Of Funds</span>
          <h2 className="card-title">Use-of-funds structure is visible today because the current budget basis is already explicit.</h2>
          <p className="card-copy" style={{ maxWidth: "60ch" }}>
            The current public package can already show where money goes in the selected development basis. It still cannot imply a full all-in capital stack, so excluded layers stay named separately.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Use-of-funds basis"
            title="Combined selected development budget"
            description="Current selected project-cost basis carried into the public underwriting read."
            columns={developmentBudgetGroup?.columns ?? ["Budget layer", "Amount"]}
            rows={buildTableRows(developmentBudgetGroup)}
            footer={
              <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                This is the current project-cost basis only. It is not yet a fully loaded land-inclusive or levered sources-and-uses schedule.
              </div>
            }
          />

          <DataTableCard
            eyebrow="Use-of-funds categories"
            title="Top-level cost structure"
            description="Main cost buckets already surfaced by the governed Financials contract."
            columns={costTopLevelGroup?.columns ?? ["Cost layer", "Amount", "Share"]}
            rows={buildTableRows(costTopLevelGroup)}
          />
        </div>

        <NumberedListCard
          eyebrow="Still outside the current public basis"
          title="Excluded layers stay explicit"
          description="These layers are intentionally not hidden inside the current public budget basis."
          items={excludedItems.map((item) => ({ label: item }))}
          tone="warning"
        />
      </section>

      <section className="stack-lg">
        <div className="stack-xs">
          <span className="card-eyebrow">What Remains Open</span>
          <h2 className="card-title">The page closes open financing items directly instead of burying them.</h2>
          <p className="card-copy" style={{ maxWidth: "60ch" }}>
            The current public package is intentionally incomplete on the sponsor package and the final financing stack. Saying that clearly builds more trust than filling the gaps with guesses.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <NumberedListCard
            eyebrow="Open Items"
            title="Still withheld or unresolved"
            description="These are the main items that still sit outside the public-ready Team & Ask package."
            items={openItems}
            tone="warning"
          />

          <section
            style={{
              borderRadius: "24px",
              padding: "24px",
              background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
            }}
          >
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">Next Investor Path</span>
                <h3 className="card-title">Use this page to close execution and capital questions, then keep pressure-testing market proof.</h3>
                <p className="card-copy">
                  Investment Case and Financials now carry the thesis and the numbers. Market Support is the next proof layer to rebuild, while Sources remains the appendix-grade trust layer.
                </p>
              </div>
              <div className="row-wrap gap-sm">
                <NavLink to="/investment-case" className="link-button link-button-primary">
                  Investment Case
                </NavLink>
                <NavLink to="/financials" className="link-button link-button-secondary">
                  Financials
                </NavLink>
                <NavLink to="/market-support" className="link-button link-button-secondary">
                  Market Support
                </NavLink>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}

export function TeamAskPage() {
  const { data, loading, error } = useContent("team-ask-page", loadTeamAskPage);

  return (
    <PageContainer
      eyebrow="Team & Ask"
      title="Execution credibility up front. Capital boundary stated honestly."
      lede="This page now shows the current public execution posture, the current project-cost funding basis, the use-of-funds structure already supported by the Financials contract, and the financing details that are still not public-ready."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading team and capital read...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <TeamAskContent data={data} /> : null}
    </PageContainer>
  );
}
